/**
 * ATAS imports — the position-episode walker, its XLSX adapter, and the
 * paired-trades fallback.
 *
 * PORTED FROM `tradecoach-saas/app/index.html` — semantics, not code (D3).
 *
 * ⚠ THIS IS A DIFFERENT ALGORITHM FROM `parseSierraFills`, and the difference
 * is the whole point. Sierra pairs fills FIFO and emits one trade per
 * (open × close) pairing — a buy-10 closed by eight qty-1 sells becomes EIGHT
 * trades. ATAS walks POSITION EPISODES: everything from flat, through
 * scale-ins and scale-outs, back to flat is ONE trade, entered at the VWAP of
 * its entry fills and exited at the VWAP of its exit fills, with the
 * underlying fills preserved in `executions[]`.
 *
 * The original's comment records why, and it is a bug already paid for: the
 * logger stamps MAE/MFE on the single fill that FLATTENS a position, measured
 * from the entry VWAP. Splitting that position into one trade per fill-pair
 * "orphaned the excursion onto whichever pair the flattening fill happened to
 * close — so scaled-out trades showed blank or wildly mis-attributed MAE/MFE,
 * and the single position was double-counted as N trades in every KPI."
 *
 * Do not "unify" the two walkers.
 */
import { consumePrior, type PriorPosition } from './carryIn';
import { getCol, numCol, numColSigned, type Row } from './columns';
import { parseDateOnly, parseTimeOnly } from './delimited';
import { excelSerialToParts, type SheetRow } from './xlsx';
import {
  buildImportedTrade, openPositionFingerprint,
  type CarriedInClosed, type Execution, type ImportedTrade, type OpenPosition,
} from './sierra';
import { findContract, stripSymbolDecorations, type ContractSpec } from '../../domain/trade';

export interface AtasFillsResult {
  trades: ImportedTrade[];
  openPositions: OpenPosition[];
  /** Rows dropped for want of a symbol, quantity or price. */
  skipped: number;
  /** Positions carried in from an earlier import that this file closed. */
  carriedInClosed?: CarriedInClosed[];
}

/** One open position episode, mid-walk. */
interface Episode {
  side: 'long' | 'short';
  /** Signed running position. Zero means flat, which ENDS the episode. */
  net: number;
  /** Total quantity ENTERED — not the same as |net| once partly closed. */
  openQty: number;
  entryNum: number;
  entryQty: number;
  exitNum: number;
  exitQty: number;
  realized: number;
  fees: number;
  firstTime: string;
  lastExitTime: string;
  contract: string;
  contractFull: string;
  dpp: number;
  account: string;
  firstFillId: string;
  lastFillId: string;
  mae: number | null;
  mfe: number | null;
  maeSecs: number | null;
  mfeSecs: number | null;
  /** Which contract's tape MAE/MFE was measured from. Usually the traded contract;
   *  on a cross-traded book (chart + data on the mini, orders filled as the micro)
   *  it is the mini. Null when the source file has no such column, which is every
   *  file written before the logger started emitting it. */
  excursionSource: string | null;
  execs: Execution[];
}

/** A source-supplied excursion column, or null when the source has none. */
function srcExcursion(row: Row, which: string): number | null {
  return getCol(row, which) !== '' ? numCol(row, which) : null;
}

/**
 * Residual (never-flattened) episodes → open positions.
 *
 * `openQty` is `|net|` — the STILL-OPEN quantity, deliberately not `openQty`
 * (the total ever entered). `avgEntry` is the VWAP over ALL entry fills and
 * `openTime` is the episode's FIRST fill, so a scaled-in position reports when
 * it started rather than when its last leg was added.
 */
export function collectOpenPositionsFromEpisodes(
  positions: Map<string, Episode>,
): OpenPosition[] {
  const out: OpenPosition[] = [];
  for (const st of positions.values()) {
    if (!st || st.net === 0) continue;
    const pos = {
      account: st.account || '',
      contract: st.contract,
      side: st.side,
      openQty: Math.abs(st.net),
      avgEntry: st.entryQty ? st.entryNum / st.entryQty : 0,
      openTime: st.firstTime,
    };
    // ⚠ Carried BESIDE the fingerprint, never inside it — see
    // `openPositionFingerprint`, which is a shared suppression key.
    out.push({
      ...pos, contractFull: st.contractFull || null, openFee: st.fees || 0,
      fingerprint: openPositionFingerprint(pos),
    });
  }
  return out;
}

/**
 * Walk per-fill rows into position episodes.
 *
 * Shared by the ATAS Executions sheet, the ATAS fills CSV, and the Trading
 * Technologies fills export — each supplies an adapter that renames its columns
 * into the shape read here.
 *
 * @param today used only when a date cell is unparseable (D26 — no wall clock)
 */
export function parseAtasFillsFromRows(
  rows: readonly Row[],
  catalogue: readonly ContractSpec[],
  today: string,
  /**
   * Positions earlier imports left open. Empty = behaviour exactly as before.
   *
   * 🚨 THIS FORMAT IS WHERE THE DEFECT IS WORST. An ATAS export has no
   * `OpenClose` column, so nothing in the file says a fill closed something
   * opened earlier — measured (#12): a −$207.50 long came out as a −$1,577.50
   * short with all three self-checks silent. A position on record is the only
   * source of truth left.
   */
  prior: readonly PriorPosition[] = [],
): AtasFillsResult {
  // ⚠ NO whitespace normalisation before `new Date(...)`, unlike the Sierra
  // parser's `fillTs`. Reproduced deliberately: an Invalid Date yields a NaN
  // comparator result, which leaves the pair in file order. The TT path relies
  // on that, because its adapter emits a date with no time.
  const sorted = rows.slice().sort((a, b) => {
    const da = new Date(getCol(a, 'Time', 'DateTime', 'Fill Time') || 0);
    const db = new Date(getCol(b, 'Time', 'DateTime', 'Fill Time') || 0);
    return (da as unknown as number) - (db as unknown as number);
  });

  const positions = new Map<string, Episode>();
  const trades: ImportedTrade[] = [];
  let skipped = 0;
  /** Prior positions this file closed — reported, and consumed at most once. */
  const carriedInClosed: CarriedInClosed[] = [];
  const priorRemaining = new Map<string, number>();

  const emitEpisode = (st: Episode): void => {
    const vwapEntry = st.entryQty ? st.entryNum / st.entryQty : 0;
    const vwapExit = st.exitQty ? st.exitNum / st.exitQty : vwapEntry;
    trades.push(buildImportedTrade({
      date: parseDateOnly(st.firstTime, today),
      timeIn: parseTimeOnly(st.firstTime),
      timeOut: parseTimeOnly(st.lastExitTime || st.firstTime),
      contract: st.contract,
      contractFull: st.contractFull,
      side: st.side,
      qty: st.openQty,
      entry: vwapEntry,
      exit: vwapExit,
      fees: st.fees,
      netPnl: st.realized - st.fees,
      // st.realized was accumulated leg-by-leg from the actual fills, against
      // the running entry VWAP at each reduce. It is ground truth; the gross
      // buildImportedTrade would rebuild from the episode's final VWAPs is a
      // lossy approximation whenever size varied mid-episode. Do not let the
      // sign repair second-guess it.
      netAuthoritative: true,
      // ⚠ The OPENING fill's $/point, while `realized` was accumulated with
      // each reducing row's own. Identical unless the symbol is respelled
      // mid-episode — see PD note in the parity test.
      dpp: st.dpp,
      sourceAccount: st.account,
      openFillId: st.firstFillId,
      closeFillId: st.lastFillId,
      maePoints: st.mae, mfePoints: st.mfe,
      maeSecs: st.maeSecs, mfeSecs: st.mfeSecs,
      excursionSource: st.excursionSource,
      executions: st.execs,
    }));
  };

  for (const r of sorted) {
    const sym = getCol(r, 'Instrument', 'Symbol');
    if (!sym) { skipped++; continue; }

    const acct = getCol(r, 'Account', 'TradeAccount');
    const dt = getCol(r, 'Time', 'DateTime', 'Fill Time');
    const dir = String(getCol(r, 'Direction', 'BuySell', 'Side', 'Action') || '').toLowerCase();
    const qty = parseInt(
      String(getCol(r, 'Volume', 'Quantity', 'FilledQuantity', 'Filled Quantity') || '0')
        .replace(/[,]/g, ''), 10);
    const px = numCol(r, 'Price', 'FillPrice', 'Fill Price', 'AveragePrice');
    const fee = numCol(r, 'Commission', 'Fees');
    if (!qty || !px) { skipped++; continue; }

    // Note the asymmetry with Sierra: anything not recognisably a buy is a
    // SELL, including an empty Direction.
    const isBuy = /^b/.test(dir) || dir === 'long' || dir === '1';
    const side: 'long' | 'short' = isBuy ? 'long' : 'short';
    const signedQty = isBuy ? qty : -qty;

    const c = findContract(sym, catalogue);
    const dpp = c?.dollarsPerPoint ?? 0;
    const contract = c?.symbol || sym.toUpperCase();
    const contractFull = stripSymbolDecorations(sym);
    const fillId = String(getCol(
      r, 'Id', 'ID', 'FillId', 'Fill Id', 'TradeId', 'Exchange ID', 'ExchangeID', 'Exchange Id',
    ) || '');

    const key = `${acct || ''}|${sym}`;
    const exRec: Execution = {
      time: parseTimeOnly(dt), side: isBuy ? 'buy' : 'sell', qty, price: px,
    };
    const exMae = srcExcursion(r, 'MAE');
    const exMfe = srcExcursion(r, 'MFE');
    const exMaeSecs = srcExcursion(r, 'MAESecs');
    // Text, not a number — so it does not go through srcExcursion.
    const exSource = String(getCol(r, 'PriceSource') || '').trim() || null;
    const exMfeSecs = srcExcursion(r, 'MFESecs');

    const st = positions.get(key);

    // Nothing open → this fill starts a fresh episode.
    if (!st || st.net === 0) {
      // ── 🚨 UNLESS IT IS CLOSING SOMETHING AN EARLIER IMPORT LEFT OPEN (#12) ──
      //
      // This is the exact line the phantom is born on. With no `OpenClose`
      // column there is nothing in the file to tell a genuine new position from
      // an overnight hold being flattened — so the only way to know is to have
      // kept the position, which is what `prior` is.
      //
      // ⚠ HOOKED HERE AND NOT ON THE FLIP-THROUGH-ZERO BRANCH BELOW. That
      // branch's leftover follows a position this very file opened and closed,
      // so there is nothing carried in for it to be closing. Hooking it too
      // would let a stale row eat a genuine reversal.
      let freshQty = qty;
      let freshFee = fee;
      if (prior.length) {
        const legs = consumePrior(prior, priorRemaining, {
          account: acct || '', contract, contractFull, side, qty, time: dt,
        });
        for (const leg of legs) {
          const p = leg.position;
          const points = p.side === 'long' ? px - p.avgEntry : p.avgEntry - px;
          const grossPnl = points * dpp * leg.qty;
          // The opening commission travelled with the position — see
          // `PriorPosition.openFee`. Without it the trade is half-priced.
          const tradeFees = leg.openFee + fee * (leg.qty / qty);
          trades.push(buildImportedTrade({
            date: parseDateOnly(p.openTime, today),
            timeIn: parseTimeOnly(p.openTime),
            timeOut: parseTimeOnly(dt),
            contract, contractFull,
            side: p.side, qty: leg.qty,
            entry: p.avgEntry, exit: px,
            fees: tradeFees, netPnl: grossPnl - tradeFees, dpp,
            sourceAccount: acct || null,
            openFillId: '', closeFillId: fillId,
            // The row's excursion belongs to the closing leg, not to a hold
            // that spanned two sessions. Blank beats confidently wrong (D173).
            maePoints: null, mfePoints: null, maeSecs: null, mfeSecs: null,
          }));
          carriedInClosed.push({
            time: dt, contract, account: acct || '',
            qty: leg.qty, openedAt: p.openTime, fingerprint: p.fingerprint,
          });
          freshQty -= leg.qty;
          freshFee -= fee * (leg.qty / qty);
        }
      }
      // Fully consumed by what was already held — nothing opens.
      if (freshQty <= 0) continue;

      positions.set(key, {
        side, net: isBuy ? freshQty : -freshQty, openQty: freshQty,
        entryNum: px * freshQty, entryQty: freshQty,
        exitNum: 0, exitQty: 0, realized: 0, fees: freshFee,
        firstTime: dt, lastExitTime: '', contract, contractFull, dpp, account: acct,
        firstFillId: fillId, lastFillId: fillId,
        mae: null, mfe: null, maeSecs: null, mfeSecs: null, excursionSource: null,
        execs: [exRec],
      });
      continue;
    }

    // Before the direction test — a scale-in advances it too.
    st.lastFillId = fillId;
    const sameDir = (st.net > 0) === isBuy;

    if (sameDir) {
      // Scale-in: extend the entry VWAP and grow the position. `st.side` is
      // NOT revisited — an episode keeps the side it opened with.
      st.execs.push(exRec);
      st.entryNum += px * qty; st.entryQty += qty;
      st.openQty += qty; st.net += signedQty;
      st.fees += fee;
      continue;
    }

    // Opposite side → reduce, and possibly flip through zero.
    let remaining = qty;
    const closeQty = Math.min(Math.abs(st.net), remaining);
    if (closeQty > 0) {
      // The RUNNING entry VWAP at this moment, not the episode's final one.
      const entryV = st.entryNum / st.entryQty;
      const pts = st.side === 'long' ? px - entryV : entryV - px;
      st.realized += pts * dpp * closeQty;
      st.exitNum += px * closeQty; st.exitQty += closeQty;
      st.lastExitTime = dt;
      st.fees += fee * (closeQty / qty);
      // Last non-null wins. The logger stamps excursion on the flattening
      // fill, so a later reducing fill's value supersedes an earlier one.
      if (exMae != null) st.mae = exMae;
      if (exMfe != null) st.mfe = exMfe;
      if (exMaeSecs != null) st.maeSecs = exMaeSecs;
      if (exSource) st.excursionSource = exSource;
      if (exMfeSecs != null) st.mfeSecs = exMfeSecs;
      st.net += isBuy ? closeQty : -closeQty;
      remaining -= closeQty;
      // ⚠ The FULL fill quantity, not closeQty. On a flip this same record is
      // also pushed into the new episode, so the closed episode's executions[]
      // overstates what it actually closed. Reproduced for parity.
      st.execs.push(exRec);
    }

    if (st.net === 0) { emitEpisode(st); positions.delete(key); }

    // Flip through zero: the leftover opens an opposite-side episode.
    if (remaining > 0) {
      positions.set(key, {
        side, net: isBuy ? remaining : -remaining, openQty: remaining,
        entryNum: px * remaining, entryQty: remaining,
        exitNum: 0, exitQty: 0, realized: 0, fees: fee * (remaining / qty),
        firstTime: dt, lastExitTime: '', contract, contractFull, dpp, account: acct,
        firstFillId: fillId, lastFillId: fillId,
        mae: null, mfe: null, maeSecs: null, mfeSecs: null, excursionSource: null,
        execs: [exRec],
      });
    }
  }

  return {
    trades, openPositions: collectOpenPositionsFromEpisodes(positions), skipped,
    carriedInClosed,
  };
}

export interface FillsIntegrity {
  /** False when the sheet is PROVABLY missing data. */
  ok: boolean;
  droppedNoPrice: number;
  /** `account|symbol` episodes that cannot be trusted. */
  keys: string[];
  /** Distinct symbols, for the user-facing message. */
  symbols: string[];
}

/**
 * Is a per-fill sheet PROVABLY INCOMPLETE?
 *
 * ⚠ A real ATAS export wrote 5 NQU6 sell fills with `Price` = 0. The walker
 * drops any row failing `!qty || !px`, and the damage is NOT confined to the
 * dropped rows — a dropped exit makes a later exit close the wrong episode. On
 * that file the preferred Executions path produced 98 trades / $2,674 where the
 * Journal sheet and ATAS's own Statistics sheet both say 164 / $3,277.75.
 *
 * The decisive signal is a row with a real QUANTITY but no PRICE.
 *
 * ⚠ Note what is deliberately NOT a signal: a non-zero buy/sell balance on its
 * own. A position genuinely open at export time produces exactly that and is
 * completely legitimate — `openPositions[]` exists for it. Treating imbalance as
 * corruption would push every user who exports mid-position onto the
 * lower-fidelity Journal path. Only a priceless fill proves missing data.
 */
export function atasFillsIntegrity(rows: readonly Row[] | null | undefined): FillsIntegrity {
  const bad = new Map<string, { symbol: string; dropped: number }>();
  let droppedNoPrice = 0;

  for (const r of rows ?? []) {
    const sym = getCol(r, 'Instrument', 'Symbol');
    if (!sym) continue;
    const qty = parseInt(
      String(getCol(r, 'Volume', 'Quantity', 'FilledQuantity', 'Filled Quantity') || '0')
        .replace(/[,]/g, ''), 10);
    if (!qty) continue;                    // no quantity: not a fill at all
    const px = numCol(r, 'Price', 'FillPrice', 'Fill Price', 'AveragePrice');
    if (px) continue;                      // priced — the normal case
    droppedNoPrice++;
    const acct = getCol(r, 'Account', 'TradeAccount');
    const key = `${acct || ''}|${sym}`;
    const e = bad.get(key) ?? { symbol: sym, dropped: 0 };
    e.dropped += qty;
    bad.set(key, e);
  }

  return {
    ok: droppedNoPrice === 0,
    droppedNoPrice,
    keys: [...bad.keys()],
    symbols: [...new Set([...bad.values()].map((e) => e.symbol))],
  };
}

/**
 * An ATAS Executions XLSX row → the shape the walker reads.
 *
 * Two translations. `Time` is an Excel date SERIAL and becomes
 * `"YYYY-MM-DD HH:MM:SS"`, which is what gives this path true second-level
 * timestamps. `Instrument` loses its `@CME` suffix.
 *
 * Returns null for an unusable row; the caller filters those out.
 */
export function adaptXlsxFillRow(r: SheetRow): Row | null {
  const serial = parseFloat(getCol(r, 'Time', 'DateTime', 'Fill Time'));
  if (!isFinite(serial)) return null;
  const parts = excelSerialToParts(serial);
  if (!parts) return null;
  const inst = String(getCol(r, 'Instrument', 'Symbol') || '').replace(/@.*$/, '').trim();
  return { ...r, Time: `${parts.date} ${parts.time}`, Instrument: inst };
}

/**
 * The ATAS "Journal" sheet — one row per round trip.
 *
 * The FALLBACK path. It is strictly less information than the Executions
 * sheet: entries are minute-rounded and a scaled-out position is collapsed
 * with a VWAP'd timestamp, so no `executions[]` survives. Used only when
 * Executions is absent or yields nothing.
 *
 * @param today used only when the open serial is unparseable (D26)
 */
export function parseAtasXlsxRows(
  rows: readonly SheetRow[],
  catalogue: readonly ContractSpec[],
  today: string,
): ImportedTrade[] {
  const trades: ImportedTrade[] = [];

  rows.forEach((r, idx) => {
    let sym = String(getCol(r, 'Instrument', 'Symbol') || '');
    sym = sym.replace(/@.*$/, '').trim();
    if (!sym) return;

    const openSerial = parseFloat(getCol(r, 'Open time', 'OpenTime', 'Open Time'));
    const closeSerial = parseFloat(getCol(r, 'Close time', 'CloseTime', 'Close Time'));
    const openPrice = numCol(r, 'Open price', 'OpenPrice', 'Open Price');
    const closePrice = numCol(r, 'Close price', 'ClosePrice', 'Close Price');
    const openVol = parseFloat(getCol(r, 'Open volume', 'OpenVolume', 'Open Volume') || '0');
    // ⚠ numColSigned, and 'PnL' BEFORE 'Price PnL' — this sheet carries BOTH
    // columns, so the order decides which number becomes the trade's P&L.
    //
    // The signed reader matters LESS than it looks, and in a way worth knowing:
    // buildImportedTrade's sign fix forces the supplied net to agree with
    // sign(gross − fees), so on a normal row it erases the difference between
    // numCol and numColSigned entirely. The distinction only survives where
    // that fix is SKIPPED — an uncatalogued symbol (dpp 0) or a scratch trade
    // (entry === exit), i.e. exactly where there is no ground truth to repair
    // the sign from. A negative control proved this: swapping in numCol passed
    // the whole suite until the corpus grew those two rows.
    const pnl = numColSigned(r, 'PnL', 'Net PnL', 'Price PnL');
    const fee = numCol(r, 'Commission', 'Fees');
    // ⚠ `||`, not `&&`. With `&&`, a row missing only the OPEN price passed both
    // guards — the close-side guard below checks `closePrice`, never `openPrice`
    // — and built a trade priced from zero: open 0 / close 5010 became 5010
    // POINTS and $250,500 gross against a real net of $50. On a LOSING row the
    // sign repair then saw gross +250,000 against a supplied −50 and flipped the
    // loss into a +$50 win. Verified by execution in both repos.
    //
    // Same corruption class D34 guards on the fills path. The Journal sheet is
    // the designated FALLBACK for files that contain priceless rows, so it
    // needed its own precondition and had none.
    if (!openPrice || !closePrice) return;

    // Skip residual open positions. ATAS writes them with the open columns
    // populated and the close columns empty; without this the parser builds a
    // phantom trade with exit 0, a blank timeOut and NaN in its synthetic id.
    // They are surfaced by the fills path's openPositions[] instead — and
    // deliberately not re-detected here, because a dismissal recorded against
    // one wouldn't match a fingerprint derived from the other.
    if (!isFinite(closeSerial) || !closePrice) return;

    const side: 'long' | 'short' = openVol < 0 ? 'short' : 'long';
    const qty = Math.abs(openVol) || 1;
    const c = findContract(sym, catalogue);
    const open = excelSerialToParts(openSerial);
    const close = excelSerialToParts(closeSerial);
    const sourceAccount = getCol(r, 'Account', 'AccountID');

    // This sheet carries no per-row id. Synthesised from serials + row index so
    // a re-import of the same file yields the same ids in the same order.
    const synthId = `atas-xlsx:${idx}:${closeSerial}:${closePrice}:${qty}`;

    trades.push(buildImportedTrade({
      date: open?.date || today,
      timeIn: open?.time || '',
      timeOut: close?.time || '',
      contract: c?.symbol || sym.toUpperCase(),
      contractFull: stripSymbolDecorations(sym),
      side, qty,
      entry: openPrice, exit: closePrice,
      fees: fee, netPnl: pnl, dpp: c?.dollarsPerPoint ?? 0,
      sourceAccount,
      openFillId: null, closeFillId: synthId,
      maePoints: null, mfePoints: null, maeSecs: null, mfeSecs: null,
    }));
  });

  return trades.filter((t) => t.contract);
}

// ---------------------------------------------------------------------------
// Sheet classification
// ---------------------------------------------------------------------------

export type SheetKind = 'atas-fills' | 'atas-trades' | 'tt-fills' | 'jigsaw';

/**
 * Which recognised shapes a sheet's headers match.
 *
 * A sheet can match more than one; the caller resolves by dispatch order.
 * Header matching is lowercase, and note the mix: the fills and TT/Jigsaw
 * predicates test EQUALITY while the paired-trades predicate uses `includes`,
 * so "Open time (UTC)" still classifies as a trades sheet.
 */
export function classifySheet(headers: readonly string[]): SheetKind[] {
  const h = headers.map((x) => x.toLowerCase());
  const has = (name: string): boolean => h.some((x) => x === name);
  const out: SheetKind[] = [];

  if (has('time') && has('direction') && has('price') && has('volume')) out.push('atas-fills');
  if (h.some((x) => x.includes('open time')) && h.some((x) => x.includes('close time'))) {
    out.push('atas-trades');
  }
  if (has('ttorderid') || (has('contract') && has('b/s') && has('fillqty'))) out.push('tt-fills');
  if (has('avgprice') && has('netpnl') && has('symbol')) out.push('jigsaw');

  return out;
}
